import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
  selectHeardProductPlanInterval,
  selectHeardProductPlanPrice,
  selectHeardProductSubscription,
  StripeInterval,
} from '@/reducers/subscription.slice'
import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  centsToCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { getDefaultPrice } from './lib/helpers'
import { TypographyStyles } from '@/styles'
import { Button, Image, Text } from '@/ui'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'

export const DiscountComplete = () => {
  const currentSubscriptionInterval =
    useReselector(selectHeardProductPlanInterval) ?? StripeInterval.month

  const isMonthly = currentSubscriptionInterval === StripeInterval.month
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const userPrice = useReselector(selectHeardProductPlanPrice)
  const sub = useReselector(selectHeardProductSubscription)
  const navigate = useNavigate()
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, false)

  const getSavedAmounts = (
    isMonthly: boolean,
    isScorp: boolean,
    userPrice: number | null
  ) => {
    const defaultPrice = getDefaultPrice(isMonthly, isScorp)
    const currentPrice = centsToCurrency(userPrice ?? defaultPrice)
    let newAmountChargedInCents
    let amountSavedInCents

    if (isMonthly) {
      // for monthly plans, we offer 50% off for 3 months
      amountSavedInCents = currentPrice.multiply(0.5)
      newAmountChargedInCents = currentPrice.subtract(amountSavedInCents)
    } else {
      amountSavedInCents = currentPrice.multiply(0.2)
      newAmountChargedInCents = currentPrice.subtract(amountSavedInCents)
      // for annual plans, we offer 20% off their next plan
    }

    return {
      newAmountChargedInCents,
      amountSavedInCents,
    }
  }

  const savedAmounts = getSavedAmounts(isMonthly, isScorp, userPrice)

  const header = isMonthly
    ? 'You are now getting 50% off your next 3 months'
    : 'You are now getting 20% your next year of Heard'

  return (
    <div className="flex flex-col items-center space-y-6">
      <div className="flex justify-center">
        <Image
          alt="Party Hat"
          src="https://heard-images.s3.amazonaws.com/assets/party-hat.svg"
          width={180}
          height={180}
        />
      </div>

      <Text
        textAlign="center"
        variant={TypographyStyles.H1}
        className="w-full md:w-1/2 mx-auto"
      >
        {header}
      </Text>

      <div className="w-full md:w-1/2 mx-auto space-y-4">
        <Text variant={TypographyStyles.BODY_LG}>
          You&apos;ll be charged{' '}
          {formatCurrencyFromCents(savedAmounts.newAmountChargedInCents)} for
          the next 3 months. You should see a confirmation email from Stripe
          soon.
        </Text>

        {isMonthly ? (
          <Text variant={TypographyStyles.BODY_LG}>
            When your subscription renews on{' '}
            {sub?.nextBillingDate &&
              DateTime.fromSeconds(sub?.nextBillingDate).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_SIMPLE
              )}
            , you&apos;ll be charged{' '}
            {formatCurrencyFromCents(savedAmounts.newAmountChargedInCents)}, a
            savings of{' '}
            {formatCurrencyFromCents(savedAmounts.amountSavedInCents)} for the
            next year. You should see a confirmation email from Stripe soon.
          </Text>
        ) : (
          <Text variant={TypographyStyles.BODY_LG}>
            We&apos;re always working to improve Heard to address our{' '}
            {replacementText}
            community&apos;s feedback, and are glad that you&apos;ll be able to
            take advantage of all your benefits.
          </Text>
        )}
      </div>

      <Button onClick={() => navigate('/')}>Go to Home</Button>
    </div>
  )
}
